import FundDetailsTabRow from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabRow';
import FundDetailsTabSkeleton from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabSkeleton';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import useHelpers from 'hooks/helpers';
import { NextImage } from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import usePayload from 'hooks/useFundDetailsRequestPayload';
import { useFetchFundRiskStatisticsTabDataQuery } from '@/store/fundDetails/services/fundDetailsApi';
import { useMemo } from 'react';
import styles from '../../styles/FundRiskStatisticsTab.module.scss';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

const FundRiskStatisticsTab = () => {
  const { getErrorDictionary } = useHelpers();
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const { shareClass, language, countryCode, correlationFundRangeId, site } = usePayload();
  const { data, error, isLoading } = useFetchFundRiskStatisticsTabDataQuery(
    {
      shareClass,
      language,
      countryCode,
      correlationFundRangeId,
      site,
      fundSettingsId: sitecoreContext.fundSettings.id,
    },
    {
      skip: !shareClass,
    }
  );

  const riskImageUrl = useMemo(() => {
    const currentRiskImage = data?.riskImageData.find(
      (item) => +item.riskLevel.value === +data?.content?.riskLevel
    );
    return currentRiskImage?.image?.src ?? '';
  }, [data]);

  const ComponentContent = () => {
    return (
      <>
        {riskImageUrl !== '' ? (
          <NextImage
            field={{ src: riskImageUrl }}
            alt="Risk image alt"
            className={styles['risk-profile-image']}
            height={400}
            width={400}
          />
        ) : (
          <div className="d-flex flex-column gap-3">
            <h4>{t('riskAndRewardProfile')}</h4>

            <div className="d-flex justify-content-between">
              <div className="subheader-2 text-start">{t('lowerRisk')}</div>
              <div className="subheader-2 text-end">{t('higherRisk')}</div>
            </div>

            <div className={styles.arrow}>
              <div className={`${styles['arrow__head']} ${styles['arrow__head--left']}`} />
              <div className={`${styles['arrow__head']} ${styles['arrow__head--right']}`} />
            </div>

            <div className="d-flex justify-content-between">
              <div className="subheader-2 text-start">{t('potentialLowerRewards')}</div>
              <div className="subheader-2 text-end">{t('potentialHigherRewards')}</div>
            </div>

            <div className="d-flex gap-2">
              {[...Array(7)].map((_, index) => (
                <div
                  className={`${styles['risk-level']} ${
                    data.content.riskLevel === index + 1 ? styles['risk-level--active'] : ''
                  }`}
                  key={index}
                >
                  <span className="h3">{index + 1}</span>
                </div>
              ))}
            </div>
            <p className="footnote text-grey">{data.content.riskProfileFootnote}</p>
          </div>
        )}
      </>
    );
  };

  return (
    <FundDetailsTabRow fullWidth className="mt-4">
      <div className="row">
        <div className="col-12 col-lg-6">
          {isLoading ? (
            <FundDetailsTabSkeleton />
          ) : error ? (
            <AlertMessageBase type="error" title={t(getErrorDictionary(error))} />
          ) : data ? (
            <ComponentContent />
          ) : (
            <div className="body-default">{t('noResultsMessage')}</div>
          )}
        </div>
      </div>
    </FundDetailsTabRow>
  );
};

export default FundRiskStatisticsTab;
